import { createSelector, select, Store } from '@ngrx/store';
import type { IEquipmentInstalment } from 'fcis-widget/dist/types/interfaces/ICalculationModel';
import type {
  IInput,
  IOutputContainer,
  IOutputs,
  IQuoteParam
} from 'fcis-widget/dist/types/interfaces/IOneCalcResponseV2';
import { distinctUntilChanged, filter, map, pipe, switchMap, type Observable } from 'rxjs';

import type { EquipmentInstalment, FinancialParameters, Output } from 'src/app/api/ocapi/models';
import { appInject } from 'src/app/app-injector';
import type { FcisRateInfo, FinancialRates } from 'src/app/shared/financing/finance.models';

import { fromCarl } from '../common/common.selectors';
import { selectMbccState } from '../mbcc/mbcc.selectors';

export const getContainer = (out: IOutputs, id: 'shortSummary' | 'fullSummary'): IOutputContainer | undefined =>
  out?.containers?.find(container => container.id === id);

const valueBasedOnFinancialRateConfigAndRateConfirmation = <T>(
  rateConfirmed: boolean,
  valueIfRatesActived: T,
  valueIfRatesDeacatived: T
): Observable<T> => {
  const store = appInject(Store);
  return store
    .select(fromCarl.selectConfig<boolean>('financial.showRates', false))
    .pipe(map(showRates => (showRates && rateConfirmed ? valueIfRatesActived : valueIfRatesDeacatived)));
};

export const mapFinanceRatesToComponents = (output: IOutputs | Output): FinancialRates =>
  ((output?.alternativeConfigurationInstalments || []) as (IEquipmentInstalment | EquipmentInstalment)[]).reduce(
    (
      financialRates: FinancialRates,
      { codeType, code, value, rawValue }: IEquipmentInstalment | EquipmentInstalment
    ) => {
      if (!!codeType && !!code && !!value && rawValue !== undefined) {
        financialRates[`${codeType}-${code}`] = {
          value,
          rawValue: typeof rawValue === 'string' ? Number.parseInt(rawValue, 10) : rawValue
        };
      }
      return financialRates;
    },
    {}
  );

export const selectIsFcisInitialized = createSelector(selectMbccState, ({ isFcisInitialized }) => isFcisInitialized);

export const selectIsFcisWidgetOpen = createSelector(selectMbccState, ({ isFcisWidgetOpen }) => isFcisWidgetOpen);

export const selectIsFcisInitializedAndWidgetOpen = createSelector(
  selectMbccState,
  ({ isFcisInitialized, isFcisWidgetOpen }) => isFcisInitialized && isFcisWidgetOpen
);

export const selectIsFcisRateConfirmed = createSelector(
  selectMbccState,
  ({ isFcisRateConfirmed }) => isFcisRateConfirmed
);

export const selectCalculationResult = createSelector(selectMbccState, ({ calculationResult }) => calculationResult);

export const selectCalculationResultOutput = createSelector(
  selectCalculationResult,
  result => result?.output || ({} as IOutputs)
);

export const selectIsFcisResultAvailable = createSelector(
  selectMbccState,
  ({ isFcisCalculationAvailable }) => isFcisCalculationAvailable
);

export const selectFcisVersion = createSelector(selectMbccState, ({ fcisVersion }) => fcisVersion);

/**
 * Returns the calculation result input (mapped to an object with id and value)
 */
export const selectMappedCalculationResultInput = createSelector(selectCalculationResult, result => ({
  input: (result?.input?.items || []).map(
    (item: IInput): FinancialParameters => ({ id: item.id, value: item.value.value })
  )
}));

export const selectFilteredCalculationResultInput = pipe(
  select(selectMappedCalculationResultInput),
  // there has to be an 'input'. without an input a calculation does not make sense.
  filter(({ input }) => !!input?.length),
  // we just want to trigger a new calculation when input has changed.
  distinctUntilChanged((prev, current) => JSON.stringify(prev.input) === JSON.stringify(current.input))
);

export const selectFinanceRates = createSelector(
  selectCalculationResultOutput,
  (output: IOutputs): FinancialRates => mapFinanceRatesToComponents(output)
);

const selectFinanceRatesAndRateConfirmed = createSelector(
  selectFinanceRates,
  selectIsFcisRateConfirmed,
  (financeRates, rateConfirmed) => ({
    financeRates,
    rateConfirmed
  })
);

/**
 * Returns rates from FCIS result if configured accordingly and rate is confirmed, emtpy object otherwise.
 */
export const selectFinanceRatesBasedOnConfigAndRateConfirmation = pipe(
  select(selectFinanceRatesAndRateConfirmed),
  switchMap(({ financeRates, rateConfirmed }) =>
    valueBasedOnFinancialRateConfigAndRateConfirmation<FinancialRates>(rateConfirmed, financeRates, {})
  )
);

export const selectFcisRateInfo = createSelector(
  selectCalculationResultOutput,
  (output: IOutputs): FcisRateInfo | undefined => {
    const getFcisSummaryContainer = (container: IOutputContainer): IQuoteParam =>
      container.items[1] ?? container.items[0];

    const shortSummaryContainer = getContainer(output, 'shortSummary');
    const summaryContainerItem = shortSummaryContainer ? getFcisSummaryContainer(shortSummaryContainer) : undefined;

    return summaryContainerItem?.label && summaryContainerItem?.value
      ? {
          rate: summaryContainerItem.value === 'null' ? null : summaryContainerItem.value, // indeed response can have 'null' - as string!
          rateAsNumber: Number.parseFloat(summaryContainerItem.businessValue), // we convert string to number
          label: summaryContainerItem.label
        }
      : undefined;
  }
);

export const selectFcisProductType = createSelector(
  selectCalculationResultOutput,
  (output: IOutputs): string | undefined => output.financingProduct?.productType
);

export const selectFcisRateInfoAndRateConfirmed = createSelector(
  selectFcisRateInfo,
  selectIsFcisRateConfirmed,
  (fcisRateInfo, rateConfirmed) => ({
    fcisRateInfo,
    rateConfirmed
  })
);

/**
 * Returns rate info if configured accordingly and rate is confirmed, undefined otherwise.
 */
export const selectFcisRateInfoBasedOnConfigAndRateConfirmation = pipe(
  select(selectFcisRateInfoAndRateConfirmed),
  switchMap(({ fcisRateInfo, rateConfirmed }) =>
    valueBasedOnFinancialRateConfigAndRateConfirmation<FcisRateInfo | undefined>(rateConfirmed, fcisRateInfo, undefined)
  )
);

export const selectFcisRateInfoLabel = createSelector(selectFcisRateInfo, fcisRateInfo => fcisRateInfo?.label || '');

/**
 * @returns fcis product type if rate is shown or undefined otherwise.
 */
export const selectFcisProductTypeOrUndefined = createSelector(
  selectIsFcisRateConfirmed,
  selectFcisProductType,
  (isFcisRateConfirmed, fcisProductType) => (isFcisRateConfirmed ? fcisProductType : undefined)
);

export const selectDisplayRate = createSelector(
  selectFcisRateInfo,
  selectIsFcisRateConfirmed,
  selectIsFcisResultAvailable,
  (fcisRateInfo, rateConfirmed, fcisResultAvailable) => fcisRateInfo && rateConfirmed && fcisResultAvailable
);
